import { ROUTES } from './routes';

/**
 * 푸시 알림 코드 → 앱 라우트 매핑.
 *
 * native 가 알림 탭 시 `RemoteMessage.data` 원본을 web 으로 전달하고(라우팅 SSOT=web),
 * web 이 본 맵으로 목적지를 결정한다. 코드 체계는 참조 앱(heviton-app-frontend)의
 * `M_HEVITON_*` 를 승계 — **BE 가 실제로 보내는 코드·목적지는 미확정이라 확인 필요**
 * (skill-api-issues). 미확정 코드는 fallback(HOME)로 흡수.
 *
 *   PI = 발전량(대시보드) / PS = 발전통계 / PE = 이상알림 / PR = 수익분석(참조 "reports" 근사)
 */
export const PUSH_CODE_TO_ROUTE: Record<string, string> = {
  M_HEVITON_PI: ROUTES.DASHBOARD,
  M_HEVITON_PS: ROUTES.STATS_GENERATION,
  M_HEVITON_PE: ROUTES.ALERTS,
  M_HEVITON_PR: ROUTES.REVENUE,
};

/**
 * 호스트 자리의 값이 **도메인인가** — 점이 있거나 `localhost` 면 도메인으로 본다.
 *
 * 커스텀 스킴은 `heviton://<host>/<path>` 로 파싱되는데, 정본 표기는 도메인을 그대로 적으므로
 * (`heviton://app.heviton.com/alerts`) 그 도메인이 호스트 자리를 차지한다. 안 걸러내면 경로가
 * `/app.heviton.com/alerts` 가 돼 라우팅이 통째로 어긋난다.
 *
 * **배포 도메인 목록을 하드코딩하지 않는 이유** — 환경마다 호스트가 다르고(운영·검증·temp·로컬 dev 서버),
 * 목록을 두면 새 환경을 추가할 때마다 여기를 같이 고쳐야 한다. 빠뜨리면 그 환경에서만 라우팅이 깨진다.
 * 우리 라우트에는 점이 들어간 세그먼트가 없어서, 「점 = 도메인」 판정으로 충분히 갈린다.
 */
const looksLikeHost = (host: string): boolean => host.includes('.') || host === 'localhost';

/**
 * `/open` 접두를 벗긴다 — 그건 **목적지가 아니라 앱으로 보내기 위한 겉포장**이기 때문이다.
 *
 * 외부 링크는 `/open/alerts` 로 뿌린다(미설치 기기에서도 화면이 뜨도록). 그 링크로 **앱이 열리면**
 * 유니버설 링크든 스킴이든 앱은 `…/open/alerts` 를 받는데, 여기서 접두를 안 벗기면 WebView 가
 * `/open/alerts` 로 가려다 **에러 화면**으로 떨어진다 — 모바일 빌드엔 `/open` 라우트가 아예 없다
 * (`createOpenRoute` 의 build target 게이트: 앱 안에서 자기 스킴을 다시 여는 루프를 막으려고 제외).
 *
 * 접두만 있고 목적지가 없으면(`/open`) null 을 돌려 호출처 fallback(대시보드)으로 접는다.
 */
function stripOpenPrefix(path: string): string | null {
  if (path !== ROUTES.OPEN && !path.startsWith(`${ROUTES.OPEN}/`)) return path;
  const rest = path.slice(ROUTES.OPEN.length);
  return rest.length > 1 ? rest : null;
}

/**
 * 링크 URL → 앱 내부 경로.
 *
 * **정본 표기(BE 계약)** — 푸시 `data.url` 과 딥링크는 웹 주소와 같은 꼴로 온다:
 *
 *   `heviton://app.heviton.com/alerts`            → `/alerts`
 *   `heviton://app.heviton.com/alerts?plantId=3`  → `/alerts?plantId=3`  (쿼리 보존)
 *
 * 이 꼴을 정본으로 삼은 건 **유니버설 링크로 전환해도 파서가 그대로**이기 때문이다 —
 * `https://app.heviton.com/alerts` 도 같은 경로로 떨어진다.
 *
 * **`/open` 접두는 벗긴다** — 외부 배포 링크의 겉포장이라 앱 안에서는 목적지가 아니다:
 *
 *   `https://app.heviton.com/open/alerts` → `/alerts`   (유니버설 링크로 앱이 열린 경우)
 *   `heviton://app.heviton.com/open/alerts` → `/alerts` (스킴으로 앱이 열린 경우)
 *
 * 아래 두 표기는 **방어적 폴백**이다(계약 위반이지만 조용히 죽지 않게):
 *
 *   `heviton://alerts` → `/alerts`   호스트 자리에 경로를 적은 경우
 *   `/alerts`          → `/alerts`   스킴 없는 내부 경로 원문
 *
 * 경로가 없으면 null — 호출처가 fallback 을 정한다(알림 탭=대시보드 / 토스트=액션 생략).
 */
export function resolveLinkPath(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  // 스킴이 없으면 내부 경로 원문으로 본다(`new URL` 은 상대 경로를 못 받는다).
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return path.length > 1 ? stripOpenPrefix(path) : null;
  }
  try {
    const { hostname, pathname, search } = new URL(trimmed);
    // 우리 도메인이 호스트면 경로만, 그 외(=호스트 자리에 경로를 적은 표기)면 호스트를 경로 앞에 되살린다.
    const base = looksLikeHost(hostname)
      ? pathname
      : `/${hostname}${pathname === '/' ? '' : pathname}`;
    const path = `${base}${search}`;
    return path.length > 1 ? stripOpenPrefix(path) : null;
  } catch {
    return null;
  }
}

/**
 * 푸시 `data` 페이로드에서 이동할 앱 내부 경로를 해석.
 *   1) `data.url` — 정본(`heviton://app.heviton.com/<path>`). 경로만 추출해 **우선** 채택.
 *   2) `data.code` — 코드 매핑(PUSH_CODE_TO_ROUTE). url 이 없을 때만.
 * 해석 불가 시 null (호출처가 fallback 결정).
 *
 * `url` 이 정본 경로라 코드 매핑보다 **표현력이 크다** — code 는 목적지가 4종으로 고정이라
 * "특정 발전소의 이상알림" 같은 대상을 못 가리키지만, url 은 쿼리까지 실을 수 있다.
 */
export function resolvePushRoute(data: Record<string, string>): string | null {
  const fromUrl = data.url ? resolveLinkPath(data.url) : null;
  if (fromUrl) return fromUrl;
  const code = data.code;
  return (code && PUSH_CODE_TO_ROUTE[code]) ?? null;
}
